export const A3C_CONTAINER_ID = 'a3c-embedded-chat-container';
export const DEFAULT_PANEL_WIDTH = 480;
export const NAV_WIDTH = 64;

/**
 * Dispatches a window resize event so page owners can reflow content.
 */
export function dispatchResizeEvent(): void {
  window.dispatchEvent(new Event('resize'));
}

/**
 * Sets the content area margin to account for the nav bar and chat panel.
 * Standard mode: margin = NAV_WIDTH + panelWidth
 * Focus mode: margin = panelWidth
 */
export function adjustContentMargin(
  panelWidth: number,
  isFocusMode: boolean,
): void {
  const contentArea = document.querySelector(
    '.app-and-pivot-wrapper',
  ) as HTMLElement | null;
  if (!contentArea) return;

  const margin = isFocusMode ? panelWidth : NAV_WIDTH + panelWidth;
  contentArea.style.marginInlineStart = `${margin}px`;
}

/**
 * Creates the fixed-position container div for the embedded chat panel.
 * Returns the existing element if it's already in the DOM, updating its
 * left position to reflect the current mode.
 */
export function createEmbeddedChatContainer(isFocusMode: boolean): HTMLElement {
  const existing = document.getElementById(A3C_CONTAINER_ID);
  if (existing) {
    existing.style.left = isFocusMode ? '0' : `${NAV_WIDTH}px`;
    return existing;
  }

  const container = document.createElement('div');
  container.id = A3C_CONTAINER_ID;
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.bottom = '0';
  container.style.left = isFocusMode ? '0' : `${NAV_WIDTH}px`;
  container.style.zIndex = '299';
  container.style.width = `${DEFAULT_PANEL_WIDTH}px`;
  container.style.display = 'none';

  document.body.appendChild(container);
  return container;
}

/**
 * Shows or hides the embedded chat container and adjusts the content margin.
 */
export function setEmbeddedChatVisible(
  visible: boolean,
  panelWidth: number = DEFAULT_PANEL_WIDTH,
  isFocusMode: boolean = false,
): void {
  const container = document.getElementById(A3C_CONTAINER_ID);
  if (container) {
    container.style.display = visible ? 'flex' : 'none';
  }

  if (visible) {
    adjustContentMargin(panelWidth, isFocusMode);
  } else {
    // Reset margin: Standard = NAV_WIDTH, Focus = 0
    adjustContentMargin(0, isFocusMode);
  }

  dispatchResizeEvent();
}

/**
 * Handles a resize of the embedded chat panel by updating the container
 * width and adjusting the content margin.
 */
export function handleEmbeddedChatResize(
  width: number,
  isFocusMode: boolean = false,
): void {
  const container = document.getElementById(A3C_CONTAINER_ID);
  if (container) {
    container.style.width = `${width}px`;
  }

  adjustContentMargin(width, isFocusMode);
  dispatchResizeEvent();
}

/**
 * Module-level focus mode state so callbacks always use the current value.
 */
let currentFocusMode = false;

/**
 * Updates the stored focus mode state. Call this whenever the mode changes.
 */
export function setCurrentFocusMode(isFocusMode: boolean): void {
  currentFocusMode = isFocusMode;
}

/**
 * Initializes window.A3CConfig with embedded mode settings.
 * Merges with any existing config (preserving page owner's panelSizes).
 * Callbacks reference the module-level focus mode so they never go stale.
 */
export function initA3CEmbeddedConfig(
  closable: boolean,
  onStateChange?: (isOpen: boolean) => void,
): void {
  const existing = (window as any).A3CConfig || {};
  (window as any).A3CConfig = {
    ...existing,
    containerId: A3C_CONTAINER_ID,
    closable,
    onClose: () => {
      setEmbeddedChatVisible(false, DEFAULT_PANEL_WIDTH, currentFocusMode);
      onStateChange?.(false);
    },
    onResize: (width: number) => {
      handleEmbeddedChatResize(width, currentFocusMode);
    },
  };
}
